import { useId } from "react";

import ExportablesTemplateHeaderSection from "@/components/AIComponents/ExportablesModal/templates/components/ExportablesTemplateHeaderSection";
import OrganizerDirections from "@/components/AIComponents/ExportablesModal/templates/components/OrganizerDirections";
import SectionInput from "@/components/AIComponents/ExportablesModal/templates/components/SectionInput";
import { TemplateTextArea } from "@/components/AIComponents/ExportablesModal/templates/components/TemplateTextArea";
import { type KnowWonderLearnData } from "@/types/models/Exportables.interface";
import { KWL_INPUT_MAX_LENGTH } from "./KnowWonderLearnChart.constants";
import {
  kwlHeaderCss,
  kwlInnerSectionCss,
  kwlSectionCss,
  kwlSectionInputCss,
  kwlTextAreaCss,
  wrapperCss,
} from "./KnowWonderLearnChart.styles";

import type { TemplateProps } from "@/components/AIComponents/ExportablesModal/templates/registry";

// Type guard to check if formData is KnowWonderLearnData
const isKnowWonderLearnData = (data: any): data is KnowWonderLearnData => {
  return (
    data &&
    typeof data === "object" &&
    ("topic" in data ||
      "articleShortTitle" in data ||
      "know" in data ||
      "wonder" in data ||
      "learned" in data ||
      "sectionOneTitle" in data ||
      "sectionTwoTitle" in data ||
      "sectionThreeTitle" in data)
  );
};

const KnowWonderLearnChart = ({
  formData,
  onChange,
  title = "KWL chart",
  isStudent = false,
}: TemplateProps<KnowWonderLearnData>) => {
  const knowId = useId();
  const wonderId = useId();
  const learnedId = useId();

  // Ensure formData is treated as KnowWonderLearnData
  const kwlData = isKnowWonderLearnData(formData)
    ? formData
    : ({} as KnowWonderLearnData);

  const directionsText = `Before reading, complete the chart by writing down what you KNOW (K) and what you WONDER (W) about ${kwlData?.topic || "this topic"}. After you read the article(s), write down what you have LEARNED (L).`;

  const { directions = directionsText } = kwlData;

  const handleChange = (field: keyof KnowWonderLearnData, value: string) => {
    if (onChange) {
      const currentDirections = formData?.directions || (directionsText ?? "");
      onChange({
        ...kwlData,
        [field]: value,
        directions: field === "directions" ? value : currentDirections,
      });
    }
  };

  return (
    <div css={wrapperCss}>
      <ExportablesTemplateHeaderSection
        templateTitle={title}
        directions={
          <OrganizerDirections
            name="KWL"
            value={directions}
            handleChange={(e) => handleChange("directions", e.target.value)}
            isStudent={isStudent}
          />
        }
        articleTitle={kwlData?.article_title || kwlData?.articleShortTitle}
      />

      <div css={kwlSectionCss}>
        <div css={kwlInnerSectionCss}>
          <div css={kwlHeaderCss}>
            <SectionInput
              isStudent={isStudent}
              align="center"
              placeholder="Know"
              value={kwlData?.sectionOneTitle || "Know"}
              onChange={(e) => handleChange("sectionOneTitle", e.target.value)}
              styleOverrides={kwlSectionInputCss}
            />
          </div>
          <TemplateTextArea
            value={kwlData?.know ?? ""}
            maxLength={KWL_INPUT_MAX_LENGTH}
          >
            <TemplateTextArea.TextArea
              id={knowId}
              data-qa-selector="exportables-kwl-know-input"
              onChange={(e) => handleChange("know", e.target.value)}
              aria-label="KWL Know Section"
              css={kwlTextAreaCss}
            />
            <TemplateTextArea.CharacterCount />
          </TemplateTextArea>
        </div>
        <div css={kwlInnerSectionCss}>
          <div css={kwlHeaderCss}>
            <SectionInput
              isStudent={isStudent}
              align="center"
              placeholder="Wonder"
              value={kwlData?.sectionTwoTitle || "Wonder"}
              onChange={(e) => handleChange("sectionTwoTitle", e.target.value)}
              styleOverrides={kwlSectionInputCss}
            />
          </div>
          <TemplateTextArea
            value={kwlData?.wonder ?? ""}
            maxLength={KWL_INPUT_MAX_LENGTH}
          >
            <TemplateTextArea.TextArea
              id={wonderId}
              data-qa-selector="exportables-kwl-wonder-input"
              onChange={(e) => handleChange("wonder", e.target.value)}
              aria-label="KWL Wonder Section"
              css={kwlTextAreaCss}
            />
            <TemplateTextArea.CharacterCount />
          </TemplateTextArea>
        </div>
        <div css={{ ...kwlInnerSectionCss, borderRight: "none" }}>
          <div css={kwlHeaderCss}>
            <SectionInput
              isStudent={isStudent}
              align="center"
              placeholder="Learned"
              value={kwlData?.sectionThreeTitle || "Learned"}
              onChange={(e) =>
                handleChange("sectionThreeTitle", e.target.value)
              }
              styleOverrides={kwlSectionInputCss}
            />
          </div>
          <TemplateTextArea
            value={kwlData?.learned ?? ""}
            maxLength={KWL_INPUT_MAX_LENGTH}
          >
            <TemplateTextArea.TextArea
              id={learnedId}
              data-qa-selector="exportables-kwl-learned-input"
              onChange={(e) => handleChange("learned", e.target.value)}
              aria-label="KWL Learned Section"
              css={kwlTextAreaCss}
            />
            <TemplateTextArea.CharacterCount />
          </TemplateTextArea>
        </div>
      </div>
    </div>
  );
};

export default KnowWonderLearnChart;
